import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, Button, Grid } from '@mui/material';
import { GridContainer, GridItem, Link, NoContent, PercentTextField, Modal } from '../../components/shared';
import { routeConstants } from '../../constants/routeConstants';
import { useStyles } from './style';
import { useHasRoles } from '../../hooks';
import { ROLE } from '../../constants/roles';
import { mapMarkerIcon } from '../../assets/images/icons';
import { MESSAGE } from '../../constants/message';
import { useSelector } from 'react-redux';
import { useCreateCreditCardFeeMutation, useGetCreditCardFeeQuery } from '../../services/CreditRate/CreditCardRate';

const displayName = 'Settings';

const Settings = () => {
  const classes = useStyles();
  const isSuperAdminOrAccounting = useHasRoles([ROLE.SUPER_ADMIN_IT, ROLE.ACCOUNTING]);
  const isSuperAdmin = useHasRoles([ROLE.SUPER_ADMIN_IT]);
  const userId = useSelector((state) => state.user.userDetails.id);

  const [openModal, setOpenModal] = useState(false);
  const [creditCardFeeRate, setCreditCardFeeRate] = useState('');
  const [updatedBy, setUpdatedBy] = useState('');
  const [updatedAt, setUpdatedAt] = useState('');
  const [error, setError] = useState(null);
  const [initialCreditCardFeeRate, setInitialCreditCardFeeRate] = useState('');

  const [updateCreditCardFee, { isLoading }] = useCreateCreditCardFeeMutation();
  const { data: creditCardFeeData } = useGetCreditCardFeeQuery();

  useEffect(() => {
    if (creditCardFeeData?.data?.repRateAndFee) {
      const creditCardFee = creditCardFeeData?.data?.repRateAndFee?.find((fee) => fee.name === 'creditCardFeeRate');

      if (creditCardFee && creditCardFee.rate >= 0) {
        setCreditCardFeeRate(creditCardFee?.rate);
        setInitialCreditCardFeeRate(creditCardFee?.rate);
      }
      if (creditCardFee) {
        setUpdatedBy(creditCardFee?.updatedByUser);
        setUpdatedAt(creditCardFee?.updatedAt);
      }
    }
  }, [creditCardFeeData]);

  const handleOpenModal = () => {
    setCreditCardFeeRate(initialCreditCardFeeRate);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleSubmit = async () => {
    try {
      await updateCreditCardFee({
        name: 'creditCardFeeRate',
        updatedBy: userId,
        rate: parseFloat(creditCardFeeRate),
      });
      handleCloseModal();
    } catch (error) {
      console.error('Error updating fee rate:', error);
      setError(error.data?.message || 'Failed to update fee rate');
    }
  };

  return (
    <React.Fragment>
      <GridContainer>
        <GridItem xs={6} display="flex" alignItems="center">
          <Typography variant="h1">Settings</Typography>
        </GridItem>
      </GridContainer>
      {isSuperAdminOrAccounting ? (
        <Box className={classes.container}>
          <Container maxWidth="xl" disableGutters className={classes.root}>
            <Link to={`/${routeConstants.SETTING_ROUTE}/${routeConstants.CURRENCY_RATES_EXCHANGE_ROUTE}`}>
              {'EXCHANGE RATES'}
            </Link>
          </Container>
          {isSuperAdmin && (
            <Container maxWidth="xl" disableGutters className={classes.root}>
              <Box>
                <Link to={`/${routeConstants.SETTING_ROUTE}/${routeConstants.INTERCARD_FEED_IMPORT}`}>
                  {'IMPORT INTERCARD FEED'}
                </Link>
              </Box>
            </Container>
          )}
          {isSuperAdminOrAccounting && (
            <Container maxWidth="xl" disableGutters className={classes.root}>
              <Link to={`/${routeConstants.SETTING_ROUTE}/${routeConstants.UPDATE_STATE_TAX_RATE}`}>
                {'UPDATE STATE TAX RATE'}
              </Link>
            </Container>
          )}
          {isSuperAdminOrAccounting && (
            <Container maxWidth="xl" disableGutters className={classes.root}>
              <Link to={`/${routeConstants.SETTING_ROUTE}/${routeConstants.MANAGE_AREA_TAX_LOCATION}`}>
                {'MANAGE AREA TAX LOCATION'}
              </Link>
            </Container>
          )}
          {isSuperAdminOrAccounting && (
            <Container maxWidth="xl" disableGutters className={classes.root}>
              <Link to={`/${routeConstants.SETTING_ROUTE}/${routeConstants.SEEDLIVE_FEED}`}>{'SEEDLIVE FEED'}</Link>
            </Container>
          )}
          {isSuperAdmin && (
            <Container maxWidth="xl" disableGutters className={classes.root}>
              <Box>
                <Link onClick={handleOpenModal} style={{ cursor: 'pointer' }}>
                  {'UPDATE TECH CREDIT CARD FEE RATE'}
                </Link>
              </Box>
            </Container>
          )}
          {isSuperAdminOrAccounting && (
            <Container maxWidth="xl" disableGutters className={classes.root}>
              <Link to={`/${routeConstants.SETTING_ROUTE}/${routeConstants.INDEPENDANT_REP}`}>
                {'MANAGE INDEPENDENT REP'}
              </Link>
            </Container>
          )}
          {isSuperAdminOrAccounting && (
            <Container maxWidth="xl" disableGutters className={classes.root}>
              <Link to={`/${routeConstants.SETTING_ROUTE}/${routeConstants.NAYAX_FEED}`}>{'NAYAX FEED'}</Link>
            </Container>
          )}
        </Box>
      ) : (
        <Container disableGutters maxWidth={'xl'} className={classes.root}>
          <NoContent
            icon={mapMarkerIcon}
            title="No Settings Found"
            desc={'There is no settings available right now!'}
          />
        </Container>
      )}

      {/* Modal for updating credit card fee rate */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        title={
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
            <Typography variant="subtitle10">Update Tech Credit Card Fee Rate</Typography>
            <Box>
              <Typography color="textSecondary" display="block">
                <Box component="span" sx={{ display: 'inline-block', minWidth: 100 }}>
                  Last Updated:
                </Box>
                {new Date(updatedAt).toLocaleDateString()}
              </Typography>
              <Typography color="textSecondary" display="block">
                <Box component="span" sx={{ display: 'inline-block', minWidth: 100 }}>
                  Updated By:
                </Box>
                {updatedBy?.firstName} {updatedBy?.lastName}
              </Typography>
            </Box>
          </Box>
        }
        titleVariant="subtitle10"
        actionButtons={
          <Box my={3}>
            <Button variant="contained" size="large" onClick={handleSubmit} disabled={isLoading}>
              {isLoading ? 'Updating...' : MESSAGE.CONFIRM}
            </Button>
            <Button size="large" onClick={handleCloseModal} variant="text" disabled={isLoading}>
              Cancel
            </Button>
          </Box>
        }
        customWidth="800px"
      >
        <Grid>
          <GridContainer>
            <GridItem xs={12}>
              <PercentTextField
                label="Tech Credit Card Fee Rate"
                fullWidth
                value={creditCardFeeRate}
                onChange={(e) => {
                  setCreditCardFeeRate(e.value);
                }}
                isSetting={true}
                error={!!error}
                helperText={error}
                disabled={isLoading}
                useFormik={false}
              />
            </GridItem>
          </GridContainer>
        </Grid>
      </Modal>
    </React.Fragment>
  );
};
Settings.displayName = displayName;
export default Settings;
